import React, { useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Container, Typography, Select, MenuItem, Box, FormControl, InputLabel, SelectChangeEvent, Card, Stack, IconButton, Button, TextField, Alert, CircularProgress, Paper } from '@mui/material';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';
import PlayArrow from '@mui/icons-material/PlayArrow';
import QeblaDirection from './QeblaDirection';
import { useLanguage } from './LanguageContext';
import { prayerLabels } from './translations';

interface PrayerTimes {
  Fajr: string;
  Dhuhr: string;
  Asr: string;
  Maghrib: string;
  Isha: string;
  Sunrise: string;
}

const PRAYER_NAMES: (keyof PrayerTimes)[] = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];

const calculationMethods = [
  { id: 0, name: 'Shia Ithna-Ansari' },
  { id: 1, name: 'University of Islamic Sciences, Karachi' },
  { id: 2, name: 'Islamic Society of North America' },
  { id: 3, name: 'Muslim World League' },
  { id: 4, name: 'Umm Al-Qura University, Makkah' },
  { id: 5, name: 'Egyptian General Authority of Survey' },
  { id: 7, name: 'Institute of Geophysics, University of Tehran' },
  { id: 8, name: 'Gulf Region' },
  { id: 9, name: 'Kuwait' },
  { id: 10, name: 'Qatar' },
  { id: 11, name: 'Majlis Ugama Islam Singapura, Singapore' },
  { id: 12, name: 'Union Organization islamic de France' },
  { id: 13, name: 'Diyanet İşleri Başkanlığı, Turkey' },
  { id: 14, name: 'Spiritual Administration of Muslims of Russia' },
  { id: 15, name: 'Moonsighting Committee Worldwide' },
  { id: 16, name: 'Dubai (unofficial)' },
];

const prayerKeys: (keyof PrayerTimes)[] = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];

function App() {
  const { lang, setLang, t } = useLanguage();
  const [prayerTimes, setPrayerTimes] = useState<PrayerTimes | null>(null);
  const [calculationMethod, setCalculationMethod] = useState<string>('2');
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [athanUrl, setAthanUrl] = useState<string>('https://islamcan.com/audio/adhan/azan1.mp3');
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [currentDateTime, setCurrentDateTime] = useState<string>('');
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
  const [latitudeInput, setLatitudeInput] = useState<string>('');
  const [longitudeInput, setLongitudeInput] = useState<string>('');
  const lastPlayedRef = useRef<string | null>(null);
  const tRef = useRef(t);
  tRef.current = t;

  const athanOptions = [
    { name: 'Athan 1', url: 'https://islamcan.com/audio/adhan/azan1.mp3' },
    { name: 'Athan 2', url: 'https://www.islamcan.com/audio/adhan/azan2.mp3' },
    { name: 'Athan 3', url: 'https://www.islamcan.com/audio/adhan/azan3.mp3' },
  ];

  useEffect(() => {
    const locale = lang === 'ar' ? 'ar' : 'en-US';
    const timer = setInterval(() => {
      setCurrentDateTime(new Date().toLocaleString(locale));
    }, 1000);
    setCurrentDateTime(new Date().toLocaleString(locale));
    return () => clearInterval(timer);
  }, [lang]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ latitude, longitude });
          setLatitudeInput(latitude.toString());
          setLongitudeInput(longitude.toString());
          setError(null);
        },
        (error) => {
          setError(`${tRef.current.errorGettingLocation}: ${error.message}`);
        }
      );
    } else {
      setError(tRef.current.errorGeoNotSupported);
    }
  }, []);

  useEffect(() => {
    if (coordinates) {
      fetch(`https://api.aladhan.com/v1/timings?latitude=${coordinates.latitude}&longitude=${coordinates.longitude}&method=${calculationMethod}`)
        .then(response => response.json())
        .then(data => {
          if (data.code === 200) {
            setPrayerTimes(data.data.timings);
            setError(null);
          } else {
            setError(tRef.current.errorFetchingTimes);
            setPrayerTimes(null);
          }
        })
        .catch(error => {
          console.error('Error fetching prayer times:', error);
          setError(tRef.current.errorFetchingTimesConn);
          setPrayerTimes(null);
        });
    }
  }, [calculationMethod, coordinates]);

  useEffect(() => {
    if (prayerTimes && isAutoPlayEnabled) {
      const interval = setInterval(() => {
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

        for (const name of PRAYER_NAMES) {
          if (prayerTimes[name] === currentTime) {
            const key = `${name}-${currentTime}`;
            if (lastPlayedRef.current !== key && audioRef.current) {
              lastPlayedRef.current = key;
              audioRef.current.play().catch(() => {});
            }
            break;
          }
        }
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [prayerTimes, isAutoPlayEnabled]);

  const playAthan = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  const handleEnableAutoPlay = () => {
    setIsAutoPlayEnabled(true);
    if (audioRef.current) {
      audioRef.current.play().catch(() => {});
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleAthanChange = (event: SelectChangeEvent<string>) => {
    setAthanUrl(event.target.value);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(audioRef.current.muted);
    }
  };

  const toggleLang = () => {
    setLang(lang === 'en' ? 'ar' : 'en');
  };

  const labels = prayerLabels[lang];

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100dvh',
        pb: 'env(safe-area-inset-bottom)',
        px: { xs: 1.5, sm: 3 },
      }}
    >
      <AppBar position="sticky" sx={{ borderRadius: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {t.appName}
          </Typography>
          <Button color="inherit" onClick={toggleLang} sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {t.langToggle}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{
        my: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: { xs: 2, sm: 3 },
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, fontWeight: 700 }}
        >
          {t.appName}
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          {currentDateTime}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          {t.allowLocation}
        </Typography>

        <Stack spacing={2} sx={{ width: '100%' }}>
          <FormControl fullWidth>
            <InputLabel id="method-select-label">{t.calculationMethod}</InputLabel>
            <Select
              labelId="method-select-label"
              id="method-select"
              value={calculationMethod}
              label={t.calculationMethod}
              onChange={e => setCalculationMethod(e.target.value as string)}
              sx={{ borderRadius: 3 }}
            >
              {calculationMethods.map(method => (
                <MenuItem key={method.id} value={method.id.toString()}>{method.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label={t.latitude}
              type="number"
              value={latitudeInput}
              onChange={e => {
                const value = e.target.value;
                setLatitudeInput(value);
                if (value.trim() !== '') {
                  const parsed = Number(value);
                  if (Number.isFinite(parsed)) {
                    setCoordinates(prev => ({ latitude: parsed, longitude: prev?.longitude ?? 0 }));
                  }
                }
              }}
              inputProps={{ step: "any" }}
            />
            <TextField
              fullWidth
              label={t.longitude}
              type="number"
              value={longitudeInput}
              onChange={e => {
                const value = e.target.value;
                setLongitudeInput(value);
                if (value.trim() !== '') {
                  const parsed = Number(value);
                  if (Number.isFinite(parsed)) {
                    setCoordinates(prev => ({ latitude: prev?.latitude ?? 0, longitude: parsed }));
                  }
                }
              }}
              inputProps={{ step: "any" }}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="athan-select-label">{t.athanSound}</InputLabel>
            <Select
              labelId="athan-select-label"
              id="athan-select"
              value={athanUrl}
              label={t.athanSound}
              onChange={handleAthanChange}
              sx={{ borderRadius: 3 }}
            >
              {athanOptions.map(option => (
                <MenuItem key={option.url} value={option.url}>{option.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: 'grey.50',
              borderRadius: 3,
            }}
          >
            <IconButton color="primary" onClick={toggleMute} size="large">
              {isMuted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
            <IconButton color="primary" onClick={playAthan} size="large">
              <PlayArrow />
            </IconButton>
          </Paper>

          <Button
            onClick={handleEnableAutoPlay}
            disabled={isAutoPlayEnabled}
            variant="contained"
            fullWidth
            size="large"
            sx={{ py: 1.5 }}
          >
            {isAutoPlayEnabled ? t.autoPlayEnabled : t.enableAutoPlay}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2, width: '100%', borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {prayerTimes ? (
          <Card variant="outlined" sx={{ mt: 3, width: '100%', overflow: 'hidden', borderRadius: 3 }}>
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t.prayerTimes}
              </Typography>
            </Box>
            <Box>
              {prayerKeys.map((key, i) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: i % 2 === 0 ? 'grey.50' : 'background.paper',
                    borderBottom: i < prayerKeys.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {labels[key]}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {prayerTimes[key]}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        ) : (
          !error && (
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t.loadingPrayerTimes}
              </Typography>
            </Box>
          )
        )}
        <audio ref={audioRef} src={athanUrl} />
      </Box>
      <QeblaDirection t={t} latitude={coordinates?.latitude ?? null} longitude={coordinates?.longitude ?? null} />
    </Container>
  );
}

export default App;
